/**
 * Admin Automations Data Hooks
 *
 * React Query hooks for the admin "Automations" tab: list + detail queries,
 * create / update / delete / state-change / run-now mutations, and the run
 * history of one automation. Every mutation invalidates the list so the table
 * refreshes on its own; the conditions metadata is invalidated too whenever
 * the set of automation slugs can change (create / delete / slug edit).
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  adminAutomationCreate,
  adminAutomationDelete,
  adminAutomationGet,
  adminAutomationRunNow,
  adminAutomationRuns,
  adminAutomationRunsAll,
  adminAutomationSetState,
  adminAutomationUpdate,
  adminAutomationsList,
} from "../utils/adminApi";
import { ADMIN_AUTOMATION_CONDITIONS_QUERY_KEY } from "./useAdminAutomationConditions";

export const ADMIN_AUTOMATIONS_QUERY_KEY = ["admin", "automations"];
export const ADMIN_AUTOMATIONS_LIST_QUERY_KEY = [...ADMIN_AUTOMATIONS_QUERY_KEY, "list"];
export const ADMIN_AUTOMATION_RUNS_QUERY_KEY = [...ADMIN_AUTOMATIONS_QUERY_KEY, "runs"];
export const ADMIN_ALL_AUTOMATION_RUNS_QUERY_KEY = [...ADMIN_AUTOMATIONS_QUERY_KEY, "runs-all"];

const automationDetailKey = (id) => [...ADMIN_AUTOMATIONS_QUERY_KEY, "detail", { id: String(id) }];
const automationRunsKey = (id, limit) => [...ADMIN_AUTOMATION_RUNS_QUERY_KEY, { id: String(id), limit }];

/**
 * List automations. `state` narrows to draft|active|paused; leave empty for all.
 * Returns the raw query — `data.results` is the array of summaries.
 */
export const useAdminAutomations = ({ state = "", enabled = true } = {}) => {
  return useQuery({
    queryKey: [...ADMIN_AUTOMATIONS_LIST_QUERY_KEY, { state }],
    queryFn: () => adminAutomationsList({ state }),
    enabled,
    refetchOnWindowFocus: false,
  });
};

/** Detail of one automation (summary + recent_runs + membership_counts). */
export const useAdminAutomation = (id, { enabled = true } = {}) => {
  return useQuery({
    queryKey: automationDetailKey(id),
    queryFn: () => adminAutomationGet(id),
    enabled: enabled && id != null && id !== "",
    refetchOnWindowFocus: false,
  });
};

/** Run history of one automation (newest first). */
export const useAdminAutomationRuns = (id, { limit = 50, enabled = true } = {}) => {
  return useQuery({
    queryKey: automationRunsKey(id, limit),
    queryFn: () => adminAutomationRuns(id, { limit }),
    enabled: enabled && id != null && id !== "",
    refetchOnWindowFocus: false,
  });
};

/**
 * Run history across EVERY automation (newest first), paged. `filters` may
 * include automation_id, status, dry_run — passed straight through to the API.
 * `keepPreviousData` keeps the table from flashing empty while a page/filter
 * change is in flight.
 */
export const useAdminAllAutomationRuns = ({ page = 1, pageSize = 25, filters = {}, enabled = true } = {}) => {
  const params = { page, page_size: pageSize, ...filters };
  return useQuery({
    queryKey: [...ADMIN_ALL_AUTOMATION_RUNS_QUERY_KEY, params],
    queryFn: () => adminAutomationRunsAll(params),
    enabled,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });
};

const useInvalidateAutomations = () => {
  const queryClient = useQueryClient();
  return async ({ id = null, slugsChanged = false } = {}) => {
    await queryClient.invalidateQueries({ queryKey: ADMIN_AUTOMATIONS_LIST_QUERY_KEY });
    if (id != null) {
      await queryClient.invalidateQueries({ queryKey: automationDetailKey(id) });
      await queryClient.invalidateQueries({ queryKey: ADMIN_AUTOMATION_RUNS_QUERY_KEY });
    }
    if (slugsChanged) {
      await queryClient.invalidateQueries({ queryKey: ADMIN_AUTOMATION_CONDITIONS_QUERY_KEY });
    }
  };
};

export const useCreateAdminAutomation = () => {
  const invalidate = useInvalidateAutomations();
  return useMutation({
    mutationFn: (payload) => adminAutomationCreate(payload),
    onSuccess: async () => {
      await invalidate({ slugsChanged: true });
    },
  });
};

export const useUpdateAdminAutomation = () => {
  const invalidate = useInvalidateAutomations();
  return useMutation({
    mutationFn: ({ id, payload }) => adminAutomationUpdate(id, payload),
    onSuccess: async (_data, variables) => {
      await invalidate({ id: variables?.id, slugsChanged: true });
    },
  });
};

export const useDeleteAdminAutomation = () => {
  const invalidate = useInvalidateAutomations();
  return useMutation({
    mutationFn: (id) => adminAutomationDelete(id),
    onSuccess: async (_data, id) => {
      await invalidate({ id, slugsChanged: true });
    },
  });
};

export const useSetAdminAutomationState = () => {
  const invalidate = useInvalidateAutomations();
  return useMutation({
    mutationFn: ({ id, state }) => adminAutomationSetState(id, state),
    onSuccess: async (_data, variables) => {
      await invalidate({ id: variables?.id });
    },
  });
};

export const useRunAdminAutomationNow = () => {
  const invalidate = useInvalidateAutomations();
  return useMutation({
    mutationFn: (id) => adminAutomationRunNow(id),
    onSuccess: async (_data, id) => {
      await invalidate({ id });
    },
  });
};
